import { useState, useRef, useCallback } from 'react';

// Restore the table only once the probe is this many CSS px past the width the
// table actually needs, so a scrollbar appearing/disappearing near the boundary
// cannot flip-flop the view.
const RESTORE_MARGIN = 24;

// Drives the desktop-table -> mobile-card switch off the table's ACTUAL horizontal
// overflow (#1465), instead of a guessed pixel breakpoint. Returns
// `[cards, probeRef]`: render cards when `cards` is true, and attach `probeRef` to a
// zero-height, full-width sentinel that stays mounted in both layouts.
//
// How it stays honest AND non-oscillating:
//   - `tableRef` points at the live desktop table container (overflow-x: auto).
//     While the table is showing, `scrollWidth > clientWidth` means the columns no
//     longer fit and a scrollbar is about to appear -> switch to cards immediately.
//   - Switching to cards unmounts the table, which would hide the overflow and flip
//     us back. So at the switch we record the width the table needed, measured off
//     the probe, and restore only once the probe grows a margin past it. One
//     measurement, no walk, no per-page magic number.
//
// The observer is attached via a CALLBACK ref, not a useEffect over a ref object.
// These pages render a <CenteredLoader/> first and only mount the real tree (with
// the probe) once data arrives; a useEffect would attach on that first loading
// commit - when the probe is still null - and never re-run, leaving overflow
// detection dead (the bug this fixes). A callback ref re-runs whenever the probe
// element mounts or unmounts, so it wires up the moment the loader clears.
//
// All widths are CSS pixels, so this tracks browser zoom and OS display scaling for
// free.
export const useOverflowCardSwitch = (tableRef) => {
  const [cards, setCards] = useState(false);
  // Probe width the table needs to fit without a scrollbar; Infinity until measured.
  const requiredWidthRef = useRef(Infinity);
  const probeElRef = useRef(null);
  const observerRef = useRef(null);

  const evaluate = useCallback(() => {
    const probe = probeElRef.current;
    if (!probe) return;
    const width = probe.clientWidth;
    if (width === 0) return; // not laid out yet

    setCards((showingCards) => {
      if (!showingCards) {
        const table = tableRef.current;
        if (table && table.scrollWidth > table.clientWidth + 1) {
          // Probe width the table must reach to fit = the content width the table
          // needs + the chrome between the probe and the table container.
          requiredWidthRef.current = table.scrollWidth + (width - table.clientWidth);
          return true;
        }
        return false;
      }
      // Showing cards: restore the table only with comfortable room to spare.
      return width < requiredWidthRef.current + RESTORE_MARGIN;
    });
  }, [tableRef]);

  const probeRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    probeElRef.current = node;
    if (node && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(evaluate);
      observer.observe(node); // fires once immediately, then on every resize
      observerRef.current = observer;
    }
  }, [evaluate]);

  return [cards, probeRef];
};
